import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * GDPR-compliant Cookie Consent Modal
 * Allows users to accept or decline cookies with detailed information
 * Styled to match the website theme and be mobile responsive
 */
const CookieConsent = ({ onAccept, onDecline }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consentStatus = localStorage.getItem('cookieConsent');
    
    // Only show the modal if no choice has been made yet
    if (!consentStatus) {
      // Small delay before showing the modal for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setIsVisible(false);
    if (onAccept) onAccept();
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setIsVisible(false);
    if (onDecline) onDecline();
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <ModalContainer
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <ModalContent>
              <ModalHeader>
                <h3 className="mb-2">{t('cookieConsent.title', 'Cookie Settings')}</h3>
                <div className="divider" style={{ margin: '1rem 0' }}></div>
              </ModalHeader>
              
              <ModalBody>
                <p>
                  {t('cookieConsent.mainText', 'We use cookies only for Google Analytics to analyze our traffic and improve your browsing experience. By clicking "Accept", you consent to our use of cookies for analytics purposes only.')}
                </p>
                
                <DetailsToggle onClick={toggleDetails}>
                  {showDetails ? t('cookieConsent.hideDetails', 'Hide Details') : t('cookieConsent.showDetails', 'Show Details')}
                </DetailsToggle>
                
                <AnimatePresence>
                  {showDetails && (
                    <CookieDetails
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CookieCategory>
                        <h4>{t('cookieConsent.necessary', 'Necessary Cookies')}</h4>
                        <p>{t('cookieConsent.necessaryDescription', 'These cookies are essential for the website to function properly and cannot be disabled.')}</p>
                      </CookieCategory>
                      
                      <CookieCategory>
                        <h4>{t('cookieConsent.analytics', 'Analytics Cookies')}</h4>
                        <p>{t('cookieConsent.analyticsDescription', 'These cookies help us understand how visitors interact with our website, helping us improve our services and provide a better user experience.')}</p>
                      </CookieCategory>
                      
                    </CookieDetails>
                  )}
                </AnimatePresence>
              </ModalBody>
              
              <ModalFooter>
                <ButtonGroup>
                  <DeclineButton onClick={handleDecline}>
                    {t('cookieConsent.decline', 'Decline')}
                  </DeclineButton>
                  <AcceptButton onClick={handleAccept}>
                    {t('cookieConsent.accept', 'Accept')}
                  </AcceptButton>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </ModalContainer>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

// Styled Components
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContainer = styled(motion.div)`
  background-color: var(--background);
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 600px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    border-radius: 16px 16px 0 0;
    max-width: 100%;
  }
`;

const ModalContent = styled.div`
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  margin-bottom: 1rem;
  
  h3 {
    margin-bottom: 0.5rem;
  }
`;

const ModalBody = styled.div`
  margin-bottom: 1.5rem;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    width: 100%;
  }
`;

const AcceptButton = styled(Button)`
  background-color: var(--primary);
  color: white;
  border: none;
  box-shadow: 0 4px 6px rgba(59, 76, 202, 0.2);
  
  &:hover {
    background-color: var(--secondary);
    box-shadow: 0 6px 8px rgba(255, 83, 80, 0.25);
  }
`;

const DeclineButton = styled(Button)`
  background-color: transparent;
  color: var(--text-dark);
  border: 1px solid var(--border);
  
  &:hover {
    background-color: var(--background-alt);
  }
`;

const DetailsToggle = styled.button`
  background: none;
  border: none;
  color: var(--primary);
  font-size: 0.9rem;
  padding: 0.5rem 0;
  cursor: pointer;
  display: block;
  margin: 0.5rem 0;
  text-decoration: underline;
  
  &:hover {
    color: var(--secondary);
  }
`;

const CookieDetails = styled(motion.div)`
  background-color: var(--background-alt);
  border-radius: 6px;
  padding: 1rem;
  margin-top: 0.5rem;
  overflow: hidden;
`;

const CookieCategory = styled.div`
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  h4 {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
  
  p {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }
`;

export default CookieConsent;
